import { StockPeriods } from 'enum/periods';
import { ApiSearchResponseData, Segment } from './common';
import { OptionData } from './options';
import { Holding, Position } from './user';
import { YearlyPnl } from './YearlyPnl';

export interface Simulations {
  simulationId: any;
  startTime: string;
  endTime: string;
  stockCount: number;
  tradeStatus: any;
  totalProfitOrLoss: number;
  holdingPnl: number;
  totalBrokerage: number;
  netProfitOrLoss: number;
  stockId: string;
  scenario: any;
  transactions: TransactionSimulation[];
  source: string;
}

export interface TransactionSimulation {
  transactonId: number;
  description: any;
  timestamp: string;
  action: string;
  price: number;
  reason: string;
  orderQuantity: number;
  profitOrLoss: number;
  brokerage: number;
  netProfitOrLoss: number;
  orderType: any;
  longDescription: any;
  holdingPnl: number;
  netProfitOrLossPercentage: number;
  interest: number;
}

export interface PassbookSimulation {
  balance: number;
  credit: number;
  debit: number;
  description: string;
  timestamp: string | null;
}
export type ProfitWithdrawals = {
  timestamp: string;
  amount: number;
};
export interface SimulationAccount {
  accountId: string;
  accountName: string;
  allowedLoss: number;
  balance: number;
  locket: boolean;
  lockedBy: string;
  lossPercentage: number;
  passbook: PassbookSimulation[];
  capitalAmount: number;
  profitWithdrawals: ProfitWithdrawals[];
}
type Config = Record<string, string>;
export interface SimulationData {
  stockPeriods: StockPeriods[];
  account: WebsocketSimulationAccount;
  executeInMarket: boolean;
  risk: number;
  backtestMode: string;
  dataSource: string;
  createdOn: string;
  withdrawProfitPercentage: number;
  progress: number;
  totalHoldingPnL: number;
  capitalAmount: number;
  stockGroup: string;
  stockGroupId: string;
  config: Config | null;
  endTime: string;
  error: any | null;
  netProfitOrLoss: number;
  scenario: string;
  scenarioDisplayName: string;
  simulationEndTime: string;
  simulationId: string;
  simulationStartTime: string;
  simulations: Simulations[];
  source: string;
  startTime: string;
  stockCount: number;
  stockIds: string[];
  totalBrokerage: number;
  totalInterest: number;
  totalProfitOrLoss: number;
  tradeStatus: SimulationStatus;
  totalDays: number;
  transactions: TransactionSimulation[];
  currency: string;
  useLeverage: boolean;
  useMTF: boolean;
  leverage: number | string;
  profitWithdrawalEnabled: boolean;
  profitWithdrawalFrequency: ProfitWithdrawalFrequency;
  withdrawalAmount: number | string;
  userIds: string[];
  stockConfig: any;
  option: boolean;
  yearlyPnl: YearlyPnl[] | [];
}

export type SimulationChartData = Record<string, TransactionSimulation[][]>;

export enum SimulationStatus {
  pending = 'pending',
  inProgress = 'inProgress',
  completed = 'completed',
  error = 'error',
  preparing = 'preparing',
  stopped = 'stopped'
}

export type SimulationProgressMap = {
  [key: string]: {
    progress: number;
    tradeStatus: SimulationStatus;
  };
};

export type BalanceAmount = { amount: number; date: string };

export interface SimulationsSearchResponse extends ApiSearchResponseData {
  searchResponse: SimulationData[];
}

export enum SimulationSource {
  HISTORIC = 'historic',
  LIVE = 'live',
  RECOMMENDATION = 'recommendation'
}

interface Indicator {
  [key: string]: string | number;
}

interface StockPeriod {
  stockPeriod: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  timestamp: string;
  indicators: Indicator;
}
export interface WebsocketSimulation {
  stockId: string;
  flags: any;
  lastTradedPrice: number;
  lastDayClose: number;
  lastTradedQuantity: number;
  lastTradedTimestamp: string;
  optionData: OptionData[];
  strikePrices: number[];
  stockPeriods: StockPeriod[];
  disabled: boolean;
  probabilityOfExecution: number;
}
export interface ConfigEntry {
  initialCapital: number;
  initialRisk: number;
  strategy: string;
  capitalAmount: number;
  risk: number;
  withdrawalProfitPercentage: number;
  debt: number;
}

export interface SimulationOrder {
  requestId: number;
  orderId: number;
  simulationId: string;
  stockId: string;
  stockName: string;
  action: string;
  price: number;
  triggerPrice: number;
  quantity: number;
  status: string;
  timestamp: string;
  optionExpiryDate: string;
  executionTimestamp: string;
  onTrigger: string;
  stopLossOrder: string;
  reason: string;
  orderType: string;
  orderGroupId: number;
  originalPrice: number;
  optionType: string;
  strikePrice: number;
  underlyingStockId: string;
  intraday: boolean;
  liveOrderId: string;
  strategy: string;
  strategyDisplayName: string;
  manualOrder: boolean;
  basketId: string;
  option: boolean;
  errorMessage: string;
  segment: Segment;
  futureExpiryDate: string;
}

export interface StockProfit {
  stockId: string;
  totalProfitOrLoss: number;
  totalBrokerage: number;
  netProfitOrLoss: number;
  holdingPnl: number;
}

export interface TransactionAccount {
  transactonId: number;
  description: string;
  optionExpiryDate: string;
  timestamp: string;
  action: string;
  price: number;
  strikePrice: number;
  optionType: string;
  reason: string;
  orderQuantity: number;
  profitOrLoss: number;
  brokerage: number;
  netProfitOrLoss: number;
  netProfitOrLossPercentage: number;
  orderType: string;
  longDescription: string;
  stockId: string;
  stockName: string;
  leverage: number;
}

export interface WebsocketSimulationAccount {
  currency: string;
  totalProfitOrLoss: number;
  hasManualIntervention: boolean;
  accountId: string;
  accountName: string | null;
  firstName: string;
  lastName: string;
  balance: number;
  passbook: PassbookSimulation[];
  config: ConfigEntry[];
  profitWithdrawals: any[];
  userId: string | null;
  positions: Position[];
  holdings: Holding[];
  orderList: any[];
  transactionList: TransactionAccount[];
  stockProfits: StockProfit[];
  totalProfitLoss: number;
  dayProfitLoss: number;
  totalBrokerage: number;
  netProfitOrLoss: number;
  totalHoldingPnL: number;
  capitalAmount: number;
  currentValue: number;
  totalInvestment: number;
  profitWithdrawalEnabled: boolean;
  profitWithdrawalFrequency: ProfitWithdrawalFrequency;
  withdrawalAmount: number | string;
  leverage: string | number;
  useLeverage: boolean;
  useMTF: boolean;
  dayPnL: number;
  enabled: boolean;
  loggedIn: boolean;
}

export interface LiveSimulationWebSocketStockData {
  simulationId: string;
  accounts: WebsocketSimulationAccount[];
  simulations: WebsocketSimulation[];
}

export interface SimulationStockAnalyticsData {
  [key: number]: { buys: number; sells: number }[];
}

type TopTrade = {
  netProfitOrLoss: number;
  stockId: string;
};
export interface SimulationTopTrades {
  topProfits: TopTrade[];
  topLosses: TopTrade[];
}

export enum ProfitWithdrawalFrequency {
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  SEMI_ANNUALLY = 'SEMI_ANNUALLY',
  ANNUALLY = 'ANNUALLY'
}

export interface OptionRiskStepResult {
  step: 'EVENTS' | 'VOLATILITY' | 'OI_STRUCTURE' | 'THETA' | 'GAMMA' | string;
  score: number;
  level: 'SAFE' | 'CAUTION' | 'NOT_SAFE' | string;
  message: string;
}

export interface NoTradeDecision {
  id: string;

  simulationId: string;
  stockId: string;
  strategyId: string | null;

  timestamp: number; // epoch millis
  dayOfWeek: number; // 0–6 (Sun–Sat)

  tradeAllowed: boolean;

  totalRiskScore: number;
  finalRiskLevel: 'SAFE' | 'CAUTION' | 'NOT_SAFE' | string;

  steps: OptionRiskStepResult[];

  spotPrice: number | null;
  targetPremium: number;
  otmLimit: number;

  message: string;
  indicators?: Map<string, number>;
}
